import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CreateSkeletons, NotificationSkeleton } from '@components/ProductSkeleton';
import { toastError } from '@components/Toast';

import { NotificationItem } from '@features/notification/components/NotificationItem';
import { ACTIVE_TAB_OBJ, ERROR_MESSAGE } from '@features/notification/utils/Utils';

import { fetchUserByEmail } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { useMyDashboardStore } from '@/store/reducers';
import { setSelectedNotificationFilterType } from '@/features/my-dashborad/redux/myDashboardSlice';

const MyDashboardNotification = ({ toggleModel, isToggleModel }) => {
	const [memberList, setMemberList] = useState([]);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [mentionNotifications, setMentionNotifications] = useState([]);
	const [hasNext, setHasNext] = useState(false);
	const { selectedNotificationFilterType: filterType } = useMyDashboardStore();
	const { unseenCount } = useSelector((store) => store.notification);

	const notificationPanelRef = useRef(null);
	const currentPageRef = useRef(0);
	const isFetchingRef = useRef(false);
	const dispatch = useDispatch();

	const fetchMemberList = async () => {
		try {
			const data = await fetchUserByEmail(`?isNitexUser=true&page=0&size=500`);
			let userList = [];
			for (let item of data) {
				userList.push({
					email: item.email,
					id: item.id,
					memberId: item.id,
					value: item.name
				});
			}
			setMemberList(userList);
		} catch (e) {
			toastError(e?.response?.data?.message);
		}
	};

	const fetchMentionNotifications = async (pageNum = 0, merge = false, isSeen = undefined) => {
		// Prevent duplicate calls
		if (isFetchingRef.current) {
			return;
		}

		const size = 20;
		const sort = 'createdAt,desc';
		const params = {
			page: pageNum,
			size,
			sort,
			frontEndNotificationEvents: ACTIVE_TAB_OBJ.mentions.enum,
			isSeen: isSeen
		};

		isFetchingRef.current = true;

		if (merge) {
			setIsLoadingMore(true);
		}

		try {
			const { data } = await ApiClient.GET('getNotifications', params);

			const result = data?.data || [];
			const currentPage = data?.currentPage ?? 0;
			const totalPage = data?.totalPages ?? 0;

			if (merge && result.length > 0) {
				setMentionNotifications((prev) => [...prev, ...result]);
			} else if (!merge) {
				setMentionNotifications(result);
			}

			setHasNext(currentPage + 1 < totalPage);

			if (result.length > 0) {
				currentPageRef.current = pageNum;
			}

			setIsLoadingMore(false);
			isFetchingRef.current = false;
		} catch ({ response }) {
			setIsLoadingMore(false);
			isFetchingRef.current = false;
			if (response && response.data && response.data.message) {
				toastError(response.data.message);
			} else {
				toastError(ERROR_MESSAGE);
			}
		}
	};

	// const getUnseenMentionCount = async () => {
	// 	try {
	// 		const { data } = await ApiClient.GET('getAllUnseenNotificationCount');

	// 	} catch ({ response }) {
	// 		if (response && response.data && response.data.message) {
	// 			toastError(response.data.message);
	// 		} else {
	// 			toastError(ERROR_MESSAGE);
	// 		}
	// 	}
	// };

	useEffect(() => {
		currentPageRef.current = 0;
		isFetchingRef.current = false;
		fetchMemberList();
		fetchMentionNotifications(0, false);
		// getUnseenMentionCount();
	}, []);

	const markRead = async (item, isSeen) => {
		let groupByKey = item?.groupByKey;
		let params = groupByKey ? `?id=${item?.id}&groupByKey=${item?.groupByKey}` : `?id=${item?.id}`;

		try {
			if (isSeen) {
				await ApiClient.POST('markNotificationUnread', {}, params);
			} else {
				await ApiClient.POST('markNotificationRead', {}, params);
			}

			setMentionNotifications((prev) =>
				prev.map((notification) =>
					notification.id === item.id ? { ...notification, isSeen: !isSeen } : notification
				)
			);

			// getUnseenMentionCount();
		} catch ({ response }) {
			if (response && response.data && response.data.message) {
				toastError(response.data.message);
			} else {
				toastError(ERROR_MESSAGE);
			}
		}
	};

	const markImportant = async (item, isImportant) => {
		let groupByKey = item?.groupByKey;
		let params = groupByKey ? `?id=${item?.id}&groupByKey=${item?.groupByKey}` : `?id=${item?.id}`;

		try {
			if (isImportant) {
				await ApiClient.POST('markNotificationImportant', {}, params);
			} else {
				await ApiClient.POST('markNotificationUnImportant', {}, params);
			}

			setMentionNotifications((prev) =>
				prev.map((notification) =>
					notification.id === item.id ? { ...notification, isImportant } : notification
				)
			);
		} catch ({ response }) {
			if (response && response.data && response.data.message) {
				toastError(response.data.message);
			} else {
				toastError(ERROR_MESSAGE);
			}
		}
	};

	const onScrollToEnd = () => {
		const wrappedElement = notificationPanelRef.current;
		if (
			wrappedElement &&
			Math.abs(wrappedElement.scrollHeight - wrappedElement.scrollTop - wrappedElement.clientHeight) < 5
		) {
			if (hasNext && !isFetchingRef.current) {
				fetchMentionNotifications(currentPageRef.current + 1, true);
			}
		}
	};

	const handleReadAndUnreadFilter = (type) => {
		dispatch(setSelectedNotificationFilterType(type));
	};

	return (
		<div className="pending-task-right-section notification-section">
			{/* Sidebar Header */}
			<div className="mentions-header bg-white d-flex align-items-center justify-content-between">
				<div className="mentions-title">
					<p className="semibold-14 gray_dark_01">
						@ Mentions:
						<span className="gray_dark_01 ml-1">{unseenCount || 0}</span>
					</p>
				</div>
				<div className="mentions-filter d-flex">
					<span
						className={`regular-14 mr-3 cursor-pointer ${filterType === 'read' ? 'gray_dark_01' : 'gray_dark_02'}`}
						onClick={(e) => {
							e.preventDefault();
							handleReadAndUnreadFilter('read');
						}}
					>
						Read
					</span>
					<span
						className={`regular-14 cursor-pointer ${filterType === 'unread' ? 'gray_dark_01' : 'gray_dark_02'}`}
						onClick={(e) => {
							e.preventDefault();
							handleReadAndUnreadFilter('unread');
						}}
					>
						Unread
					</span>
				</div>
			</div>

			{/* Mentions List */}
			<div
				className="mentions-list notification-body scroll-y-label mt-0"
				ref={notificationPanelRef}
				onScroll={onScrollToEnd}
			>
				{mentionNotifications.length > 0 ? (
					<>
						{mentionNotifications.map((item, i) => (
							<NotificationItem
								key={i}
								item={item}
								markRead={markRead}
								markImportant={markImportant}
								toggleModel={toggleModel}
								isToggleModel={isToggleModel}
								memberList={memberList}
							/>
						))}
						{isLoadingMore && (
							<CreateSkeletons iterations={3}>
								<NotificationSkeleton />
							</CreateSkeletons>
						)}
					</>
				) : (
					<div className="empty">
						<p>No mentions found</p>
					</div>
				)}
			</div>
		</div>
	);
};

export default MyDashboardNotification;
